import { useEffect, useMemo, useState } from "react";
import useTitanApp from "../../hooks/useTitanApp";
import useTitanTeam from "../../hooks/useTitanTeam";
import {
  getDocumentsAnalyticsByCompanyId,
  addCompanyFolder,
  getCompanyFolders,
  renameCompanyFolder,
  addCompanyDocument,
  getDocumentsOverallCompliancesByCompanyId,
  getDocumentsMembersCompliancesByCompanyId,
  viewCompanyDocument,
  getCompanyDocumentsByCompanyId,
  getCompanyDocumentById,
  updateCompanyDocument,
  deleteCompanyDocuments,
} from "@/dispatchers/companyDocumentsDispatchers";
import {
  addorUpdateDocumentQuestions,
  setViewingDoc,
  updateDocumentcomplienceStatus,
  getAllDocumentsByEmail,
  UpdateMultipleDocumentComplianceStatus,
  updateDocumentAssignments,
  setFolder,
} from "@/dispatchers/documentDispatchers";
import {
  updateConsultationActions,
  setAddPatientAIChatMessage,
  SaveAIChatMessage,
  getPatientDocuments,
  getPatientDocumentById,
} from "@/dispatchers/patientDispatchers";
import { v4 as uuidv4 } from "uuid";
import { ObjectId } from "bson";
import { IntercomTrack } from "@/data/IntercomTrack";
import {
  intercomActionDocumentAddText,
  intercomActionDocumentUpdateText,
} from "@/data/constantStrings";
import config from "../../../src/config";
interface Assignment {
  email: string;
  compliant: boolean;
  completedAt?: string;
  quizCompleted?: boolean;
  markAsRead?: boolean;
  quizScore?: number;
  fullName?: string;
}

type DocumentCategory =
  | "Referral"
  | "Consultation Summary"
  | "Vaccination Certificate"
  | "Prescription"
  | "Other"
  | "SOP"
  | "PGD"
  | "HR"
  | "Auditing"
  | "Compliance"
  | "Clincal"
  | "General";

export interface DocumentItem {
  _id?: string;
  id: string;
  title: string;
  type: DocumentCategory;
  createdAt: string;
  sizeKB: number;
  content: string;
  assignments: Assignment[];
  reviewInterval?: string;
  lastReviewedAt?: string;
  lastReviewedBy?: string;
  folder?: string;
  folderId?: string;
  fileUrl?: string;
  mainFile?: DocumentFile;
  quiz?: {
    questions: {
      id: string;
      question: string;
      options: string[];
      correctIndex: number;
    }[];
  };
}

interface DocumentFile {
  fileName: string;
  fileURL: string;
  sizeKB: number | null;
  base64File?: string | null;
}

// Payload helpers (enums)
const folderEnumMapper: Record<string, number> = {
  GDPR: 1,
  Operations: 2,
  PGD: 3,
  Patients: 4,
  ProfessionalStandards: 5,
  Profit: 6,
  SOP: 7,
  Staff: 8,
};

export const documentTypeEnumMapper: Record<string, number> = {
  Referral: 1,
  ConsultationSummary: 2,
  VaccinationCertificate: 3,
  Prescription: 4,
  Other: 5,
  SOP: 6,
  PGD: 7,
  HR: 8,
  Auditing: 9,
  Compliance: 10,
  Clinical: 11, // ✅ fix spelling
  General: 12,
};
export const reviewIntervalEnumMapper: Record<string, number> = {
  Never: 1,
  "Every week": 2,
  "Every 2 weeks": 3,
  "Every month": 4,
  "Every 3 months": 5,
  "Every 6 months": 6,
  "Every year": 7,
  "Every 2 years": 8,
};
export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
}
export interface GeneratedQuiz {
  questions: QuizQuestion[];
}

export type QuizDifficulty = "easy" | "normal" | "hard";

export interface QuizModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (quiz: GeneratedQuiz) => void;
  initialQuiz?: GeneratedQuiz | null;
  docTitle: string;
  docType?: string;
  createdAt?: string;
  sizeKB?: number;
  initialText?: string;
  docId?: string;
}

const enumToDocumentType: Record<number, string> = {
  1: "Referral",
  2: "Consultation Summary",
  3: "Vaccination Certificate",
  4: "Prescription",
  5: "Other",
  6: "SOP",
  7: "PGD",
  8: "HR",
  9: "Auditing",
  10: "Compliance",
  11: "Clinical",
  12: "General",
};

const enumToReviewInterval: Record<number, string> = {
  1: "Never",
  2: "Every week",
  3: "Every 2 weeks",
  4: "Every month",
  5: "Every 3 months",
  6: "Every 6 months",
  7: "Every year",
  8: "Every 2 years",
};

function mapFolderNameToEnum(folderName?: string): number | null {
  if (!folderName) return null;
  return folderEnumMapper[folderName] ?? null;
}
function mapEnumToFolderName(enumValue?: number): string | null {
  if (enumValue == null) return null;
  const entry = Object.entries(folderEnumMapper).find(
    ([, value]) => value === enumValue,
  );
  return entry ? entry[0] : null;
}

function mapDocumentTypeToEnum(docType?: string): number | null {
  if (!docType) return null;
  return documentTypeEnumMapper[docType] ?? null;
}
function mapEnumToDocumentType(enumValue?: number): string | null {
  if (enumValue == null) return null;
  const entry = Object.entries(documentTypeEnumMapper).find(
    ([, value]) => value === enumValue,
  );
  return entry ? entry[0] : null;
}

function mapReviewIntervalToEnum(interval?: string): number | null {
  if (!interval) return null;
  return reviewIntervalEnumMapper[interval] ?? null;
}
function mapEnumToReviewInterval(enumValue?: number): string | null {
  if (enumValue == null) return null;
  const entry = Object.entries(reviewIntervalEnumMapper).find(
    ([, value]) => value === enumValue,
  );
  return entry ? entry[0] : null;
}

// Types mirrored from NewDocumentModal without importing UI
type NewDocCategory = DocumentCategory;
export interface GeneratedQuiz {
  questions: {
    id: string;
    question: string;
    options: string[];
    correctIndex: number;
  }[];
}
interface NewDocumentData {
  title: string;
  type: NewDocCategory;
  mainFile: File;
  supportingFiles: File[];
  assignedEmails: string[];
  reviewInterval: string;
  folder?: string;
  quiz?: GeneratedQuiz;
  roles: string[];
}
interface UpdateDocumentData {
  title: string;
  type: NewDocCategory;
  mainFile?: File | null;
  supportingFiles?: File[];
  assignedEmails: string[];
  reviewInterval: string;
  folder?: string;
  quiz?: GeneratedQuiz;
  roles: string[];
}

const toBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const useDocumentsData = () => {
  const [initialMembers, setInitialMembers] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [activeDocumentsTab, setActiveDocumentsTab] = useState<
    "admin" | "my" | "analytics"
  >("admin");
  const [expandedTreeNode, setExpandedTreeNodes] = useState<
    Record<string, boolean>
  >({});

  const {
    memberState,
    appState,
    onboardingVerificationState,
    companyDocumentState,
    documentState,
    patientState,
  } = useTitanApp();
  const { teamState } = useTitanTeam();

  const { consultationId, documentCompleted } = patientState || {};
  const { membersData, isMemberLoading } = memberState || {};
  const { userData } = onboardingVerificationState || {};
  const {
    darkMode,
    currentPharmacyId,
    companyId,
    currentUser,
    currentPharmacy,
    company,
    features,
  } = appState || {};
  const { ifCalculatorAcitonsEnabled } = features;
  const { documentQuestions, viewingDoc, myDocumentsData, folder } =
    documentState || {};
  const { roles } = teamState || ({} as any);
  const {
    companyDocumentsData,
    isCompanyDocumentsLoading,
    isAddDocumentLoading,
    isDocumentsAnalyticsLoading,
    documentsAnalytics,
    isDocumentsOverallComplianceLoading,
    documentsOverallCompliance,
    isDocumentsMembersComplianceLoading,
    documentsMembersCompliance,
    companyFoldersLoader,
    companyUserDocumentsData,
  } = companyDocumentState || ({} as any);
  const { documentSizeLimitInMbs } = config;
  const [companyFolders, setCompanyFolders] = useState<any[]>([]);
  const [isCompanyFoldersLoading, setIsCompanyFoldersLoading] = useState(false);
  const [companyDocuments, setCompanyDocuments] = useState<DocumentItem[]>([]);
  const [companyUserDocuments, setCompanyUserDocuments] = useState<
    DocumentItem[]
  >([]);
  const [myDocuments, setMyDocuments] = useState<DocumentItem[]>([]);
  const [isMyDocumentsLoading, setIsMyDocumentsLoading] = useState(false);
  const currentUserEmail: string | undefined = useMemo(() => {
    return currentUser?.emailAddress || undefined;
  }, [currentUser]);
  const refreshdocuments = async () => {
    const result = await getCompanyDocumentsByCompanyId(companyId);
    return result;
  };

  const refreshDocumentQuizData = async (docId: string) => {
    // Refresh all documents to ensure quiz data is latest
    await refreshdocuments();
    // Return the specific document with updated quiz data
    const updated = companyDocuments.find(
      (d) => d._id === docId || d.id === docId,
    );
    return updated;
  };
  useEffect(() => {
    if (membersData) {
      const mappedMembers = membersData.map((m: any) => ({
        id: m.userId,
        title: m.title != null ? m.title.toString() : "0",
        forename: m.firstName || "",
        surname: m.lastName || "",
        email: m.emailAddress || "",
        role: m.role?.roleName || "",
        isClinician: m.isClinician || false,
        active: m.isActive || false,
      }));
      setInitialMembers(mappedMembers);
    }
  }, [membersData]);

  useEffect(() => {
    if (roles) {
      const mappedRoles = roles.map((r: any) => ({
        id: r._id,
        role: r.roleName || "",
      }));
      setAvailableRoles(mappedRoles);
    }
  }, [roles]);

  useEffect(() => {
    if (companyDocumentsData && companyDocumentsData.length > 0) {
      const mapped = companyDocumentsData.map((pd: any) => {
        // Map quiz questions from API response to your format
        const quiz =
          pd.questions && pd.questions.length > 0
            ? {
                questions: pd.questions.map((q: any) => ({
                  id:
                    q.questionId ||
                    `q-${Math.random()
                      .toString(36)
                      .slice(2, 9)}`,
                  question: q.question || "",
                  options: q.options || [],
                  correctIndex: q.correctIndex || 0,
                })),
              }
            : undefined;
        return {
          _id: pd?._id,
          id: pd?._id || pd?.documentId || "", // Use _id as primary ID
          title: pd?.title || "",
          type: (mapEnumToDocumentType(pd?.documentType) ||
            "General") as DocumentCategory,
          createdAt: pd?.createdOn || pd?.createdAt,
          sizeKB: pd?.sizeKB || 0,
          content: pd?.content || "",
          folder:
            pd?.folderName || mapEnumToFolderName(pd?.folder) || undefined,
          folderId:
            pd?.folderId ||
            pd?.folderID ||
            pd?.companyFolderId ||
            (typeof pd?.folder === "string" ? pd.folder : undefined),
          reviewInterval:
            mapEnumToReviewInterval(pd?.reviewInterval) || undefined,
          lastReviewedAt: pd?.lastReviewedAt || undefined,
          lastReviewedBy: pd?.lastReviewedBy || undefined,
          fileUrl: pd?.mainFile?.fileURL || undefined,
          assignments: Array.isArray(pd?.assignedToMembers)
            ? pd.assignedToMembers.map((m: any) => ({
                email: m?.emailAddress || "",
                compliant: !!m?.compliant,
                completedAt: m?.completedAt || undefined,
                quizCompleted: !!m?.quizCompleted,
                markAsRead: m?.markAsRead || false,
                fullName: m?.fullName || undefined,
                quizScore:
                  typeof m?.quizScore === "number" ? m.quizScore : undefined,
              }))
            : [],
          quiz: quiz,
          mainFile: pd?.mainFile,
          supportingFiles: pd?.supportingFiles,
        } as DocumentItem;
      });
      setCompanyDocuments(mapped);
    } else {
      setCompanyDocuments([]);
    }
  }, [companyDocumentsData]);

  useEffect(() => {
    if (companyUserDocumentsData && companyUserDocumentsData.length > 0) {
      const mapped = companyUserDocumentsData.map((pd: any) => {
        // Map quiz questions from API response to your format
        const quiz =
          pd.questions && pd.questions.length > 0
            ? {
                questions: pd.questions.map((q: any) => ({
                  id:
                    q.questionId ||
                    `q-${Math.random()
                      .toString(36)
                      .slice(2, 9)}`,
                  question: q.question || "",
                  options: q.options || [],
                  correctIndex: q.correctIndex || 0,
                })),
              }
            : undefined;
        return {
          _id: pd?._id,
          id: pd?._id || pd?.documentId || "", // Use _id as primary ID
          title: pd?.title || "",
          type: (mapEnumToDocumentType(pd?.documentType) ||
            "General") as DocumentCategory,
          createdAt: pd?.createdOn || pd?.createdAt,
          sizeKB: pd?.sizeKB || 0,
          content: pd?.content || "",
          folder:
            pd?.folderName || mapEnumToFolderName(pd?.folder) || undefined,
          reviewInterval:
            mapEnumToReviewInterval(pd?.reviewInterval) || undefined,
          lastReviewedAt: pd?.lastReviewedAt || undefined,
          lastReviewedBy: pd?.lastReviewedBy || undefined,
          fileUrl: pd?.mainFile?.fileURL || undefined,
          assignments: Array.isArray(pd?.assignedToMembers)
            ? pd.assignedToMembers.map((m: any) => ({
                email: m?.emailAddress || "",
                compliant: !!m?.compliant,
                completedAt: m?.completedAt || undefined,
                quizCompleted: !!m?.quizCompleted,
                markAsRead: m?.markAsRead || false,
                fullName: m?.fullName || undefined,
                quizScore:
                  typeof m?.quizScore === "number" ? m.quizScore : undefined,
              }))
            : [],
          quiz: quiz,
          mainFile: pd?.mainFile,
          supportingFiles: pd?.supportingFiles,
        } as DocumentItem;
      });
      setCompanyUserDocuments(mapped);
    } else {
      setCompanyUserDocuments([]);
    }
  }, [companyUserDocumentsData]);

  // Actions
  const handleDocumentTabsClick = (id: string) => {
    setActiveDocumentsTab(id as any);

    if (id === "analytics") {
      getDocumentsAnalyticsByCompanyId(companyId);
      getDocumentsOverallCompliancesByCompanyId(companyId);
      getDocumentsMembersCompliancesByCompanyId(companyId);
    }

    if (id === "my") {
      fetchMyDocuments();
    }
    if (id === "admin") {
      fetchMyDocuments();
    }
  };

  // Fetch My Documents from API by current user email
  const fetchMyDocuments = async () => {
    setIsMyDocumentsLoading(true);
    try {
      const result = await getAllDocumentsByEmail();

      // Extract assignedDocuments from the user object and get the user's email
      let documents: any[] = [];
      let userEmail: string = "";

      if (result) {
        if (Array.isArray(result)) {
          // If result is an array of users, flatten assignedDocuments from all users
          result.forEach((user: any) => {
            documents.push(...(user?.assignedDocuments || []));
            if (!userEmail && user?.emailAddress) {
              userEmail = user.emailAddress;
            }
          });
        } else if (
          result.assignedDocuments &&
          Array.isArray(result.assignedDocuments)
        ) {
          // If result is a single user object with assignedDocuments
          documents = result.assignedDocuments;
          userEmail = result?.emailAddress || "";
        }
      }

      if (documents.length > 0) {
        const mapped = documents.map((pd: any) => {
          const quiz =
            pd.questions && pd.questions.length > 0
              ? {
                  questions: pd.questions.map((q: any) => ({
                    id:
                      q.questionId ||
                      `q-${Math.random()
                        .toString(36)
                        .slice(2, 9)}`,
                    question: q.question || "",
                    options: q.options || [],
                    correctIndex: q.correctIndex || 0,
                  })),
                }
              : undefined;

          return {
            _id: pd?._id || pd?.documentId,
            id: pd?._id || pd?.documentId || "",
            title: pd?.title || "",
            type: (mapEnumToDocumentType(pd?.documentType) ||
              "General") as DocumentCategory,
            createdAt: pd?.createdOn || pd?.createdAt,
            sizeKB: pd?.sizeKB || 0,
            content: pd?.content || "",
            folder:
              pd?.folderName || mapEnumToFolderName(pd?.folder) || undefined,
            reviewInterval:
              mapEnumToReviewInterval(pd?.reviewInterval) || undefined,
            lastReviewedAt: pd?.lastReviewedAt || undefined,
            lastReviewedBy: pd?.lastReviewedBy || undefined,
            fileUrl: pd?.mainFile?.fileURL || undefined,
            assignments: [
              {
                email: userEmail || currentUserEmail || "",
                compliant: !!pd?.compliant,
                completedAt: pd?.completedAt || undefined,
                quizCompleted: !!pd?.quizCompleted,
                markAsRead: pd?.markAsRead,
                quizScore:
                  typeof pd?.quizScore === "number" ? pd.quizScore : undefined,
              },
            ],
            quiz: quiz,
            mainFile: pd?.mainFile,
            supportingFiles: pd?.supportingFiles,
          } as DocumentItem;
        });
        setMyDocuments(mapped);
      } else {
        setMyDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching my documents:", error);
      setMyDocuments([]);
    } finally {
      setIsMyDocumentsLoading(false);
    }
  };

  const handleCreateDocument = async (data: NewDocumentData) => {
    const selectedRoleNames: string[] = data?.roles || [];
    const selectedRoleIds = (roles || [])
      .filter((r: any) => selectedRoleNames.includes(r.roleName))
      .map((r: any) => r._id);

    const selectedMembers = (membersData || []).filter((m: any) =>
      selectedRoleIds.includes(m?.role?._id),
    );

    const mappedMembers = selectedMembers.map((m: any) => ({
      userId: m._id,
      title: m.title || 0,
      emailAddress: m.emailAddress || "",
      role: m.role,
      isClinician: m.isClinician || false,
      isActive: m.isActive || false,
      fullName: `${m.firstName || ""} ${m.lastName || ""}`.trim(),
    }));

    let mainDocFile: DocumentFile | null = null;
    if (data?.mainFile) {
      const file: File = data.mainFile;
      const base64String = await toBase64(file);
      mainDocFile = {
        fileName: file.name,
        fileURL: "",
        sizeKB: Math.round(file.size / 1024),
        Base64File: base64String,
      };
    }

    let supportingDocFiles: DocumentFile[] = [];
    if (data?.supportingFiles?.length > 0) {
      const files: File[] = Array.from(data.supportingFiles);
      supportingDocFiles = await Promise.all(
        files.map(async (file) => ({
          fileName: file.name,
          fileURL: "",
          sizeKB: Math.round(file.size / 1024),
          base64File: await toBase64(file),
        })),
      );
    }

    const totalSize =
      (data.mainFile?.size || 0) +
      (data.supportingFiles || []).reduce((s, f) => s + f.size, 0);
    const sizeKB = Math.max(1, Math.round(totalSize / 1024));
    const id = `doc-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const detailLines: string[] = [
      `Title: ${data.title}`,
      `Main: ${data.mainFile?.name}`,
      (data.supportingFiles || []).length
        ? `Supporting: ${(data.supportingFiles || [])
            .map((f) => f.name)
            .join(", ")}`
        : "Supporting: none",
      data.folder ? `Folder: ${data.folder}` : undefined,
      `Review: ${data.reviewInterval}`,
      data.quiz?.questions?.length
        ? `Quiz: ${data.quiz.questions.length} question(s) generated`
        : undefined,
    ].filter(Boolean) as string[];

    const newDoc: DocumentItem = {
      id,
      title: data.title,
      type: data.type,
      createdAt: new Date().toISOString(),
      sizeKB,
      content: detailLines.join("\n"),
      assignments: (data.assignedEmails || []).map((email) => ({
        email,
        compliant: false,
      })),
      reviewInterval: data.reviewInterval,
      folder: data.folder,
      quiz: data.quiz,
    };

    const payload = {
      companyId: companyId,
      documentId: newDoc.id,
      title: newDoc.title,
      documentType: mapDocumentTypeToEnum(newDoc.type),
      sizeKB: newDoc.sizeKB,
      content: newDoc.content,
      reviewInterval: mapReviewIntervalToEnum(newDoc.reviewInterval),
      folderId: newDoc.folder,
      mainFile: mainDocFile,
      supportingFiles: supportingDocFiles,
      questions: null,
      assignedToRoles: selectedRoleIds,
      assignedToMembers: mappedMembers,
    };

    setCompanyDocuments((prev) => [newDoc, ...prev]);
    try {
      await addCompanyDocument(payload);
      const refreshResult = await getCompanyDocumentsByCompanyId(companyId);
      await IntercomTrack(
        currentUser,
        currentPharmacy,
        company,
        intercomActionDocumentAddText,
      );
      return refreshResult;
    } catch (e) {
      // Revert optimistic update on error
      setCompanyDocuments((prev) => prev.filter((d) => d.id !== id));
      throw e;
    }
  };

  const updateDocument = (id: string, data: UpdateDocumentData) => {
    setCompanyDocuments((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        let sizeKB = d.sizeKB;
        if (data.mainFile) {
          const newSupportSize = (data.supportingFiles || []).reduce(
            (sum, f) => sum + f.size,
            0,
          );
          sizeKB = Math.max(
            1,
            Math.round(((data.mainFile as any).size + newSupportSize) / 1024),
          );
        }
        const lines = (d.content || "").split("\n");
        const setLine = (prefix: string, value: string) => {
          const idx = lines.findIndex((l) => l.startsWith(prefix));
          if (idx !== -1) lines[idx] = `${prefix}${value}`;
          else lines.push(`${prefix}${value}`);
        };
        setLine("Title: ", data.title);
        if (data.mainFile) setLine("Main: ", (data.mainFile as File).name);
        if (data.supportingFiles && data.supportingFiles.length)
          setLine(
            "Supporting: ",
            data.supportingFiles.map((f) => f.name).join(", "),
          );
        if ((data as any).quiz && (data as any).quiz?.questions?.length)
          setLine(
            "Quiz: ",
            `${(data as any).quiz.questions.length} question(s) generated`,
          );
        if ((data as any).folder)
          setLine("Folder: ", (data as any).folder as string);
        setLine("Review: ", data.reviewInterval);

        return {
          ...d,
          title: data.title,
          type: data.type as DocumentItem["type"],
          sizeKB,
          content: lines.join("\n"),
          assignments: data.assignedEmails.map((email) => ({
            email,
            compliant: false,
          })),
          reviewInterval: data.reviewInterval,
          folder: (data as any).folder,
          quiz: (data as any).quiz,
        } as DocumentItem;
      }),
    );
  };

  const saveQuizToDocument = async (id: string, quiz: GeneratedQuiz) => {
    // 🔹 Step 1: Optimistic update
    setCompanyDocuments((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        const lines = (d.content || "").split("\n");
        const setLine = (prefix: string, value: string) => {
          const idx = lines.findIndex((l) => l.startsWith(prefix));
          if (idx !== -1) lines[idx] = `${prefix}${value}`;
          else lines.push(`${prefix}${value}`);
        };
        setLine("Quiz: ", `${quiz.questions.length} question(s) generated`);
        return {
          ...d,
          quiz,
          content: lines.join("\n"),
        } as DocumentItem;
      }),
    );

    // 🔹 Step 2: Prepare payload according to backend model
    const payload = {
      _id: id, // matches C# model field
      questions: quiz.questions.map((q) => ({
        id: q.id,
        question: q.question,
        options: q.options,
        correctIndex: q.correctIndex,
      })),
    };

    // 🔹 Step 3: Call API
    try {
      await addorUpdateDocumentQuestions(payload);
    } catch (error) {
      console.error("❌ Failed to update company document questions:", error);

      // 🔹 Optional rollback if API fails
      setCompanyDocuments((prev) =>
        prev.map((d) => {
          if (d.id !== id) return d;
          const filtered = (d.content || "")
            .split("\n")
            .filter((l) => !l.startsWith("Quiz: "));
          const next = { ...d } as DocumentItem;
          delete next.quiz;
          return { ...next, content: filtered.join("\n") };
        }),
      );
    }
  };

  const removeQuizFromDocument = (id: string) => {
    setCompanyDocuments((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        const lines = (d.content || "")
          .split("\n")
          .filter((l) => !l.startsWith("Quiz: "));
        const next = { ...d } as DocumentItem;
        delete next.quiz;
        return { ...next, content: lines.join("\n") } as DocumentItem;
      }),
    );
  };

  const markDocumentsReviewed = (ids: string[], reviewerEmail: string) => {
    const now = new Date().toISOString();
    setCompanyDocuments((prev) =>
      prev.map((d) =>
        ids.includes(d.id)
          ? { ...d, lastReviewedAt: now, lastReviewedBy: reviewerEmail }
          : d,
      ),
    );
  };
  const handleAddFolder = async (folderName: string) => {
    if (!folderName) return;
    try {
      setIsCompanyFoldersLoading(true);
      let newDocumentID = new ObjectId().toString();
      const payload = {
        _id: newDocumentID,
        folder: folderName,
        companyId: companyId,
      };
      const response = await addCompanyFolder(payload);

      setFolder(newDocumentID);

      // ✅ Ensure latest folder list is fetched after adding
      await handleGetFolders(); // 🔥 add this line
    } catch (error) {
      console.error("Error adding folder:", error);
    } finally {
      setIsCompanyFoldersLoading(false);
    }
  };

  const handleGetFolders = async () => {
    try {
      setIsCompanyFoldersLoading(true);
      const response = await getCompanyFolders(companyId);
      if (Array.isArray(response)) {
        setCompanyFolders(response);
      } else {
        setCompanyFolders([]);
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
      setCompanyFolders([]);
    } finally {
      setIsCompanyFoldersLoading(false);
    }
  };

  const handleRenameFolder = async (folderId: string, folderName: string) => {
    if (!folderId || !folderName.trim()) return null;

    try {
      setIsCompanyFoldersLoading(true);
      const response = await renameCompanyFolder({
        id: folderId,
        folder: folderName.trim(),
      });

      await Promise.all([
        handleGetFolders(),
        getCompanyDocumentsByCompanyId(companyId),
        getDocumentsAnalyticsByCompanyId(companyId),
        getDocumentsOverallCompliancesByCompanyId(companyId),
        getDocumentsMembersCompliancesByCompanyId(companyId),
      ]);

      return response;
    } catch (error) {
      console.error("Error renaming folder:", error);
      return null;
    } finally {
      setIsCompanyFoldersLoading(false);
    }
  };

  const handleTreeNodesClick = async (doc: any) => {
    if (viewingDoc?.url?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(viewingDoc.url);
      } catch {}
    }

    if (!doc?._id || !doc?.mainFile?.fileName) {
      console.warn("❌ No main file found for this document:", doc);
      setViewingDoc(null);
      return;
    }

    const details = await fetchDocumentById(doc._id, doc.mainFile.fileName, 1);

    if (details) {
      if (details?.base64Content?.startsWith("data:")) {
        const mimeType = details.base64Content
          .split(",")[0]
          .split(":")[1]
          .split(";")[0];
        const base64Data = details.base64Content.split(",")[1];

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);

        setViewingDoc({
          title: details.fileName,
          url: blobUrl,
          mimeType: mimeType,
        });
      }
    } else {
      setViewingDoc(null);
    }
  };

  // Fetch a document's Base64 content using dispatcher
  const fetchDocumentById = async (
    docId: string,
    fileName: string,
    type: number,
  ) => {
    try {
      // Ensure the file has an extension
      const hasExt = /\.[a-zA-Z0-9]+$/.test(fileName);
      const fileWithExtension = hasExt ? fileName : `${fileName}.pdf`;

      const payload = {
        _id: docId,
        companyId: companyId,
        filesType: type, // usually 1 for main file
        fileName: fileWithExtension,
      };

      const res = await viewCompanyDocument(payload);

      // ✅ API now returns base64File that already includes the "data:..." prefix
      const rawBase64 = res?.base64File || "";

      // Strip prefix if it already includes data URI
      const alreadyHasPrefix = rawBase64.startsWith("data:");
      const base64Content = alreadyHasPrefix
        ? rawBase64 // leave it as-is
        : `data:${getMimeTypeFromFileName(
            fileWithExtension,
          )};base64,${rawBase64}`;

      if (rawBase64) {
        return {
          base64Content,
          mimeType: getMimeTypeFromFileName(fileWithExtension),
          fileName: fileWithExtension,
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching document details:", error);
      return null;
    }
  };

  const getMimeTypeFromFileName = (fileName: string) => {
    const ext = fileName
      .split(".")
      .pop()
      ?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "application/pdf";
      case "txt":
        return "text/plain";
      case "doc":
      case "docx":
        return "application/msword";
      case "png":
        return "image/png";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      default:
        return "application/octet-stream";
    }
  };

  // Download a document by converting its base64 to a downloadable file
  const downloadDocumentById = async (
    docId: string,
    fileName: string,
    type: number,
  ) => {
    try {
      const details = await fetchDocumentById(docId, fileName, type);
      if (!details?.base64Content) {
        console.warn("⚠️ No base64 content found for file:", fileName);
        return false;
      }

      // Handle if API already includes "data:" prefix
      const base64Url = details.base64Content.startsWith("data:")
        ? details.base64Content
        : `data:${details.mimeType};base64,${details.base64Content}`;

      // Create a link and trigger download
      const link = document.createElement("a");
      link.href = base64Url;
      link.download = details.fileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true;
    } catch (error) {
      console.error("❌ Error downloading document:", error);
      return false;
    }
  };

  const getCompanyDocumentByIdData = async (docId: string) => {
    try {
      const response = await getCompanyDocumentById(docId);
      return response;
    } catch (error) {
      console.error("Error fetching document by ID:", error);
      return null;
    }
  };

  const handleUpdateDocument = async (
    docId: string,
    data: UpdateDocumentData,
  ) => {
    try {
      // 🟢 Prepare main file (handle both new File and existing mock)
      let mainDocFile: DocumentFile | null = null;
      if (data?.mainFile instanceof File) {
        // ✅ User selected a new file ��� convert to Base64
        const base64String = await toBase64(data.mainFile);
        mainDocFile = {
          fileName: data.mainFile.name,
          fileURL: "",
          sizeKB: Math.round(data.mainFile.size / 1024),
          base64File: base64String,
        };
      } else if (data?.mainFile && (data.mainFile as any).name) {
        // ⚠️ Old file (user didn’t replace) → send empty fileName
        mainDocFile = {
          fileName: "",
          fileURL: "",
          sizeKB: 0,
          base64File: null,
        };
      } else {
        // ⚠️ No file at all
        mainDocFile = {
          fileName: "",
          fileURL: "",
          sizeKB: 0,
          base64File: null,
        };
      }

      // 🟢 Prepare supporting files safely
      let supportingDocFiles: DocumentFile[] = [];
      if (
        Array.isArray(data.supportingFiles) &&
        data.supportingFiles.length > 0
      ) {
        supportingDocFiles = await Promise.all(
          data.supportingFiles.map(async (file) => {
            if (file instanceof File) {
              // ✅ New supporting file
              return {
                fileName: file.name,
                fileURL: "",
                sizeKB: Math.round(file.size / 1024),
                base64File: await toBase64(file),
              };
            } else {
              // ⚠️ Existing supporting file (unchanged) → send empty filename
              return {
                fileName: "",
                fileURL: "",
                sizeKB: 0,
                base64File: null,
              };
            }
          }),
        );
      }

      // 🔹 Map roles → IDs
      const selectedRoleNames: string[] = data?.roles || [];
      const selectedRoleIds = (roles || [])
        .filter((r: any) => selectedRoleNames.includes(r.roleName))
        .map((r: any) => r._id);

      const selectedMembers = (membersData || []).filter((m: any) =>
        selectedRoleIds.includes(m?.role?._id),
      );

      const mappedMembers = selectedMembers.map((m: any) => ({
        userId: m._id,
        title: m.title || 0,
        firstName: m.firstName || "",
        lastName: m.lastName || "",
        emailAddress: m.emailAddress || "",
        role: m.role,
        isClinician: m.isClinician || false,
        isActive: m.isActive || false,
      }));

      // 🔹 Calculate total file size (safe)
      const totalSize =
        (data.mainFile instanceof File ? data.mainFile.size : 0) +
        (data.supportingFiles || []).reduce(
          (s, f) => s + ((f as any).size || 0),
          0,
        );
      const sizeKB = Math.max(1, Math.round(totalSize / 1024));

      // 🔹 Build content summary
      const detailLines: string[] = [
        `Title: ${data.title}`,
        `Main: ${mainDocFile?.fileName || "none"}`,
        supportingDocFiles.length
          ? `Supporting: ${supportingDocFiles
              .map((f) => f.fileName || "none")
              .join(", ")}`
          : "Supporting: none",
        data.folder ? `Folder: ${data.folder}` : undefined,
        `Review: ${data.reviewInterval}`,
        data.quiz?.questions?.length
          ? `Quiz: ${data.quiz.questions.length} question(s)`
          : undefined,
      ].filter(Boolean) as string[];

      // 🔹 Construct final payload
      const payload = {
        _id: docId,
        companyId: companyId,
        title: data.title,
        documentType: mapDocumentTypeToEnum(data.type),
        sizeKB: sizeKB,
        content: detailLines.join("\n"),
        reviewInterval: mapReviewIntervalToEnum(data.reviewInterval),
        folderId: data.folder,
        mainFile: mainDocFile,
        supportingFiles: supportingDocFiles,
        questions: data.quiz?.questions || null,
        assignedToRoles: selectedRoleIds,
        assignedToMembers: mappedMembers,
      };

      // 🔹 Call API
      const response = await updateCompanyDocument(payload);

      // 🔹 Refresh
      await getCompanyDocumentsByCompanyId(companyId);
      await IntercomTrack(
        currentUser,
        currentPharmacy,
        company,
        intercomActionDocumentUpdateText,
      );
    } catch (error) {
      console.error("❌ Error updating document:", error);
    }
  };
  const handleReview = async (ids: string[]) => {
    try {
      const companyDocumentComplianceRequestModels = ids.map((id) => ({
        _id: id,
      }));
      await UpdateMultipleDocumentComplianceStatus(
        companyDocumentComplianceRequestModels,
      );
      await getCompanyDocumentsByCompanyId(companyId);
      return true;
    } catch (error) {
      console.error("Error updating document compliance:", error);
      return false;
    }
  };

  const handleDeleteCompanyDocuments = async (docIds: string[]) => {
    try {
      const payload = {
        documentIds: docIds,
      };
      const response = await deleteCompanyDocuments(payload);
      await getCompanyDocumentsByCompanyId(companyId);
      return response;
    } catch (error) {
      console.error("Error deleting documents:", error);
      return null;
    }
  };

  return {
    // data
    initialMembers,
    isMemberLoading,
    darkMode,
    availableRoles,
    isCompanyDocumentsLoading,
    isAddDocumentLoading,
    companyDocuments,
    setCompanyDocuments,
    myDocuments,
    isMyDocumentsLoading,
    currentUserEmail,
    activeDocumentsTab,
    isDocumentsAnalyticsLoading,
    documentsAnalytics,
    isDocumentsOverallComplianceLoading,
    documentsOverallCompliance,
    isDocumentsMembersComplianceLoading,
    documentsMembersCompliance,
    expandedTreeNode,
    // actions
    handleCreateDocument,
    updateDocument,
    saveQuizToDocument,
    removeQuizFromDocument,
    markDocumentsReviewed,
    handleDocumentTabsClick,
    setActiveDocumentsTab,
    fetchDocumentById,
    downloadDocumentById,
    getCompanyDocumentByIdData,
    handleUpdateDocument,
    handleTreeNodesClick,
    setExpandedTreeNodes,
    handleDeleteCompanyDocuments,
    ///folders
    companyFolders,
    isCompanyFoldersLoading,
    handleAddFolder,
    handleGetFolders,
    handleRenameFolder,
    //enum
    enumToDocumentType,
    enumToReviewInterval,
    mapEnumToDocumentType,
    mapEnumToFolderName,
    mapEnumToReviewInterval,
    //questions
    documentQuestions,
    companyDocumentsData,
    refreshDocumentQuizData,
    setViewingDoc,
    viewingDoc,
    updateDocumentcomplienceStatus,
    updateDocumentAssignments,
    refreshdocuments,
    myDocumentsData,
    handleReview,
    companyFoldersLoader,
    folder,
    setFolder,
    consultationId,
    updateConsultationActions,
    documentCompleted,
    setAddPatientAIChatMessage,
    SaveAIChatMessage,
    getPatientDocumentById,
    getCompanyDocumentsByCompanyId,
    documentSizeLimitInMbs,
    currentUser,
    ifCalculatorAcitonsEnabled,
    companyId,
    companyUserDocuments,
  };
};
